import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, RefreshCw, ArrowLeft, Download } from "lucide-react";
import { SITE } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "avishi_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-sm border border-line bg-surface/50 p-8 md:p-10">
        <p className="font-heading text-2xl font-semibold tracking-[0.12em] text-ink">{SITE.name}<span className="text-accent">.</span></p>
        <p className="mt-1 text-xs uppercase tracking-[0.22em] font-bold text-stone">Enquiries Dashboard</p>
        <label htmlFor="admin-password" className="mt-8 block text-xs uppercase tracking-[0.18em] font-bold text-stone mb-1">
          Password
        </label>
        <input
          id="admin-password"
          data-testid="admin-password-input"
          type="password"
          autoComplete="current-password"
          className="input-line"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(""); }}
        />
        {error && <p data-testid="admin-login-error" role="alert" className="mt-2 text-xs text-accent font-semibold">{error}</p>}
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="mt-6 w-full bg-ink text-ivory font-semibold tracking-wide px-6 py-3.5 hover:bg-accent transition-colors duration-300 disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [enquiries, setEnquiries] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { headers });
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Could not load enquiries.");
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  if (!token) return <Login onLogin={setToken} />;

  const toggleRead = async (enq) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${enq.id}`, { read: !enq.read }, { headers });
      setEnquiries((list) => list.map((e) => (e.id === enq.id ? { ...e, read: !enq.read } : e)));
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (enq) => {
    try {
      await axios.delete(`${API}/admin/enquiries/${enq.id}`, { headers });
      setEnquiries((list) => list.filter((e) => e.id !== enq.id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const downloadCsv = () => {
    if (!enquiries?.length) return;
    const cols = ["created_at", "name", "business_name", "email", "phone", "business_type", "looking_for", "budget", "message", "read"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...enquiries.map((e) => cols.map((c) => esc(c === "read" ? (e.read ? "yes" : "no") : e[c])).join(","))];
    const url = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const unread = (enquiries || []).filter((e) => !e.read).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen">
      <header className="border-b border-line">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <div className="flex items-baseline gap-4">
            <p className="font-heading text-2xl font-semibold tracking-[0.12em] text-ink">{SITE.name}<span className="text-accent">.</span></p>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-stone">Enquiries</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-stone hover:text-ink transition-colors duration-300">
              <ArrowLeft size={15} /> View site
            </a>
            <button data-testid="admin-refresh-button" onClick={load} aria-label="Refresh enquiries" className="p-2 text-stone hover:text-ink transition-colors duration-300">
              <RefreshCw size={17} />
            </button>
            <button
              data-testid="admin-export-csv-button"
              onClick={downloadCsv}
              disabled={!enquiries?.length}
              className="inline-flex items-center gap-2 border border-ink/30 text-ink text-sm font-semibold px-4 py-2 hover:bg-ink hover:text-ivory transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={15} /> Export CSV
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 border border-ink/30 text-ink text-sm font-semibold px-4 py-2 hover:bg-ink hover:text-ivory transition-colors duration-300">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h1 className="font-heading font-medium tracking-tight text-4xl md:text-5xl text-ink">Enquiries</h1>
          {enquiries && (
            <p data-testid="admin-enquiry-count" className="text-sm text-stone">
              {enquiries.length} total · <span className="text-accent font-semibold">{unread} unread</span>
            </p>
          )}
        </div>

        {enquiries === null ? (
          <p className="mt-16 text-stone">Loading…</p>
        ) : enquiries.length === 0 ? (
          <p data-testid="admin-empty-state" className="mt-16 text-stone">No enquiries yet. New contact-form submissions will appear here.</p>
        ) : (
          <ul className="mt-10 border-t border-line">
            {enquiries.map((enq) => (
              <li
                key={enq.id}
                data-testid={`admin-enquiry-${enq.id}`}
                className={`border-b border-line py-6 grid md:grid-cols-12 gap-4 ${enq.read ? "opacity-60" : ""}`}
              >
                <div className="md:col-span-8">
                  <div className="flex items-center gap-3 flex-wrap">
                    {!enq.read && <span className="h-2 w-2 bg-accent inline-block" aria-label="Unread" />}
                    <p className="font-heading text-2xl text-ink">{enq.name}{enq.business_name ? <span className="text-stone text-lg"> — {enq.business_name}</span> : null}</p>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone">
                    <a href={`mailto:${enq.email}`} className="inline-flex items-center gap-1.5 hover:text-accent transition-colors duration-300"><Mail size={13} /> {enq.email}</a>
                    {enq.phone && <a href={`tel:${enq.phone}`} className="inline-flex items-center gap-1.5 hover:text-accent transition-colors duration-300"><Phone size={13} /> {enq.phone}</a>}
                    {enq.budget && <span>Budget: {enq.budget}</span>}
                    {enq.looking_for && <span>{enq.looking_for}</span>}
                  </div>
                  {enq.business_type && <p className="mt-2 text-sm text-stone"><span className="font-semibold text-ink/70">Business:</span> {enq.business_type}</p>}
                  <p className="mt-2 text-base text-ink/85 leading-relaxed max-w-2xl">{enq.message}</p>
                </div>
                <div className="md:col-span-4 flex md:flex-col items-start md:items-end gap-3">
                  <p className="text-xs text-stone">{fmtDate(enq.created_at)}</p>
                  <div className="flex gap-2">
                    <button
                      data-testid={`admin-read-toggle-${enq.id}`}
                      onClick={() => toggleRead(enq)}
                      className="text-xs font-semibold uppercase tracking-wide border border-ink/25 px-3 py-1.5 hover:bg-ink hover:text-ivory transition-colors duration-300"
                    >
                      {enq.read ? "Mark unread" : "Mark read"}
                    </button>
                    <button
                      data-testid={`admin-delete-${enq.id}`}
                      onClick={() => remove(enq)}
                      aria-label={`Delete enquiry from ${enq.name}`}
                      className="p-1.5 border border-ink/25 text-stone hover:text-accent hover:border-accent transition-colors duration-300"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
